import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ZoneBadge } from "./ZoneBadge";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { useUnansweredCount } from "@/features/enquiries/useUnanswered";

/**
 * The shell.
 *
 * The zone badge sits in the header beside the product name, where somebody
 * looking for "which deployment am I changing?" will find it without being told
 * where to look.
 */

const NAV = [
  { to: "/", labelKey: "nav.dashboard" },
  { to: "/enquiries", labelKey: "nav.enquiries" },
  { to: "/institutions", labelKey: "nav.institutions" },
  { to: "/users", labelKey: "nav.users" },
  { to: "/legal", labelKey: "nav.legal" },
  { to: "/ai", labelKey: "nav.ai" },
  { to: "/retention", labelKey: "nav.retention" },
  { to: "/health", labelKey: "nav.health" },
  { to: "/migrations", labelKey: "nav.migrations" },
  { to: "/support", labelKey: "nav.support" },
] as const;

export function Layout() {
  const t = useT();
  const unanswered = useUnansweredCount();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-paper-warm">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50
                   focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:text-ink"
      >
        {t("nav.skipToContent")}
      </a>

      <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-paper-deep bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="side-nav"
          aria-label={t("nav.menu")}
          className="rounded p-2 text-ink-soft hover:bg-paper-warm md:hidden"
        >
          <span aria-hidden="true">☰</span>
        </button>

        <div className="flex min-w-0 flex-1 items-baseline gap-3">
          <span className="font-display text-lg text-ink">{t("app.name")}</span>
          <span className="text-sm text-ink-muted">{t("app.admin")}</span>
        </div>

        <ZoneBadge />
        <LanguageSwitcher />

        <div className="flex items-center gap-3 border-l border-paper-deep pl-3">
          <p className="hidden text-sm text-ink sm:block">{user?.displayName}</p>
          <button
            type="button"
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
            className="rounded px-2 py-1 text-sm text-ink-muted hover:bg-paper-warm hover:text-ink"
          >
            {t("nav.signOut")}
          </button>
        </div>
      </header>

      <div className="flex">
        <aside
          id="side-nav"
          className={`w-56 shrink-0 border-r border-paper-deep bg-paper-warm md:block ${
            menuOpen ? "block" : "hidden"
          }`}
        >
          <nav aria-label={t("nav.menu")} className="flex flex-col gap-0.5 p-3">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-white font-medium text-ink shadow-sm"
                      : "text-ink-soft hover:bg-white/60 hover:text-ink"
                  }`
                }
              >
                <span className="flex items-center justify-between gap-2">
                  <span>{t(item.labelKey)}</span>
                  {item.to === "/enquiries" && unanswered > 0 ? (
                    /*
                      The number a person opens the console for. It is the
                      unanswered count, not "unread": an enquiry is dealt with
                      when somebody has answered it, not when somebody has
                      looked at it.
                    */
                    <span
                      className="rounded-full bg-cue-amber px-2 py-0.5 text-xs font-medium text-ink"
                      aria-label={t("nav.enquiries.unanswered", { count: unanswered })}
                    >
                      {unanswered}
                    </span>
                  ) : null}
                </span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main id="main" className="min-w-0 flex-1 px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
